<script lang="ts">
  /**
   * L'écran Santé : ce qui se vide et se remplit pendant une séance.
   *
   * Les deux Jauges y sont dessinées **jusqu'à leur plafond théorique** — vingt
   * emplacements — les cases que le personnage n'a pas encore en pointillés.
   * Sur la fiche compacte, c'était trop large ; ici, c'est la place.
   */
  import Emplacements from '../../composants/Emplacements.svelte'
  import Chiffres from '../../composants/Chiffres.svelte'
  import NombreSaisi from '../../composants/NombreSaisi.svelte'
  import type { CibleEcrite, Compteur, CompteursVue, Fiche } from '../../rules'

  let {
    vue,
    c,
    ajuster,
    gestes,
    part,
  }: {
    vue: Fiche
    c: CompteursVue
    ajuster: (cible: Compteur, delta: number) => void
    gestes: {
      statut: (id: string) => void
      folie: (id: string) => void
      affliction: (libelle: string) => void
      retirerAffliction: (index: number) => void
      /** Corrige le libellé d'une Affliction de la table. Ses chiffres restent. */
      renommerAffliction: (index: number, libelle: string) => void
      /** Chiffre une Affliction : « −30 en Séduction ». */
      chiffrerAffliction: (
        index: number,
        cible: CibleEcrite,
        id: string,
        valeur: number,
      ) => void
      /** Change la valeur d'un chiffre déjà posé, sa cible inchangée. */
      corrigerChiffreAffliction: (index: number, position: number, valeur: number) => void
      effacerChiffreAffliction: (index: number, position: number) => void
    }
    part: (valeur: number, total: number) => number
  } = $props()

  const subis = $derived(new Set(vue.statuts.map((s) => s.id)))
  const folles = $derived(
    new Set(vue.afflictions.filter((a) => a.du_livre).map((a) => a.nom)),
  )

  /** L'Affliction que la table invente, le temps de la saisir. */
  let inventee = $state('')

  function ajouterInventee() {
    gestes.affliction(inventee)
    inventee = ''
  }

  /** Ce que le Statut laisse faire quand on est attaqué, dit en clair. */
  const defense = (r: string) =>
    r === 'aucune' ? 'aucune défense' : r === 'esquiveseule' ? 'esquive seule' : ''

</script>

<section
  class="compteurs"
  class:critique={c.etat_vital === 'critique'}
  class:mort={c.etat_vital === 'mort'}
>
  <div class="cartouche vie">
    <div class="bandeau">Points de vie</div>
    <div class="corps">
      <p class="compte gros">
        <NombreSaisi
          valeur={c.pv.valeur}
          min={c.pv.min}
          max={c.pv.max}
          libelle="Points de vie"
          taille="grand"
          onchange={(v) => ajuster('points_de_vie', v - c.pv.valeur)}
        />
        <span class="total">/ {c.pv.max}</span>
      </p>
      <div
        class="barre vitale"
        role="progressbar"
        aria-valuenow={c.pv.valeur}
        aria-valuemin={c.pv.min}
        aria-valuemax={c.pv.max}
      >
        <i style:width="{part(c.pv.valeur, c.pv.max)}%"></i>
      </div>
      <p class="pied">
        Jusqu'à {c.pv.theorique} Points de vie, Vitalité poussée au bout.
      </p>
    </div>
  </div>

  <div class="cartouche fortune">
    <div class="bandeau">Chance</div>
    <div class="corps">
      <div class="jauge">
        <p class="compte seule">
          {c.chance.valeur} <span class="max">/ {c.chance.max}</span>
        </p>
        <Emplacements
          valeur={c.chance.valeur}
          max={c.chance.max}
          libelle="Chance"
          famille="chance"
          onchange={(v) => ajuster('chance', v - c.chance.valeur)}
        />
      </div>
    </div>
  </div>

  <div class="cartouche mental">
    <div class="bandeau">Santé mentale</div>
    <div class="corps">
      <div class="jauge">
        <div class="entete">
          <span class="titre">Jauge de Stress</span>
          <span class="compte">
            {c.stress.valeur} <span class="max">/ {c.stress.max}</span>
          </span>
        </div>
        <Emplacements
          valeur={c.stress.valeur}
          max={c.stress.max}
          jusqua={c.stress.theorique}
          graduations
          libelle="Points de Stress"
          famille="stress"
          onchange={(v) => ajuster('stress', v - c.stress.valeur)}
        />
      </div>

      <div class="jauge">
        <div class="entete">
          <span class="titre">Contamination</span>
          <span class="compte">
            {c.contamination.valeur} <span class="max">/ {c.contamination.max}</span>
          </span>
        </div>
        <Emplacements
          valeur={c.contamination.valeur}
          max={c.contamination.max}
          jusqua={c.contamination.theorique}
          graduations
          libelle="Points de Contamination"
          famille="contam"
          onchange={(v) => ajuster('contamination', v - c.contamination.valeur)}
        />
      </div>

      <p class="pied">
        Les cases en pointillés sont celles que la Jauge n'a pas encore : elle
        gagne un emplacement tous les 5 de la Compétence qui la porte, jusqu'à
        vingt.
      </p>
    </div>
  </div>
</section>

<div class="cartouche statuts">
  <div class="bandeau">Statuts actifs</div>
  <div class="corps">
    {#if vue.statuts.length === 0}
      <p class="vide">Aucun. Le personnage est d'aplomb.</p>
    {:else}
      <ul class="subis">
        {#each vue.statuts as s (s.id)}
          <li>
            <span class="nom">
              {s.nom}
              {#if defense(s.reaction_defensive)}
                <span class="badge statut">{defense(s.reaction_defensive)}</span>
              {/if}
              {#if s.automatique}<span class="badge rang">imposé</span>{/if}
            </span>
            {#if !s.automatique}
              <button type="button" class="lien" onclick={() => gestes.statut(s.id)}>retirer</button>
            {/if}
            <span class="resume">{s.resume}</span>
          </li>
        {/each}
      </ul>
    {/if}

    <!-- Douze pastilles identiques ne disaient rien : ce que chaque Statut
         fait vivait dans une infobulle, donc nulle part sur un téléphone. -->
    <div class="offre">
      {#each vue.statuts_offerts as s (s.id)}
        <button
          type="button"
          class="offert"
          aria-pressed={subis.has(s.id)}
          onclick={() => gestes.statut(s.id)}
        >
          <span class="titre">
            {s.nom}
            {#if defense(s.reaction_defensive)}
              <span class="badge statut">{defense(s.reaction_defensive)}</span>
            {/if}
          </span>
          <span class="effet">{s.resume}</span>
        </button>
      {/each}
    </div>
  </div>
</div>

<div class="cartouche">
  <div class="bandeau">Afflictions</div>
  <div class="corps">
    {#if vue.afflictions.length === 0}
      <p class="vide">Aucune. L'esprit tient bon.</p>
    {:else}
      <ul class="subis">
        {#each vue.afflictions as a (a.index)}
          <li>
            <span class="nom">
              {#if a.du_livre}
                {a.nom}
              {:else}
                <!-- Ce que la table a écrit se corrige en place : on raccourcit
                     « Brûler −10 CHA −30 Séduction » en « Brûler » une fois
                     les chiffres posés, sans rien perdre. -->
                <input
                  class="saisie libelle"
                  type="text"
                  value={a.libelle}
                  aria-label="Libellé de l'Affliction"
                  onchange={(e) => gestes.renommerAffliction(a.index, e.currentTarget.value)}
                />
              {/if}
              {#if a.incurable}<span class="badge statut">incurable</span>{/if}
              {#if !a.du_livre}<span class="badge rang">de la table</span>{/if}
              <button
                type="button"
                class="lien"
                onclick={() => gestes.retirerAffliction(a.index)}
              >
                retirer
              </button>
            </span>
            {#if a.effet}<span class="resume">{a.effet}</span>{/if}
            <!-- Ce qu'elle coûte, en chiffres. Le libellé d'une Affliction
                 maison les portait en prose, et aucun n'atteignait un jet. -->
            <Chiffres
              poses={a.modificateurs}
              ajouter={(cible, id, valeur) =>
                gestes.chiffrerAffliction(a.index, cible, id, valeur)}
              corriger={(position, valeur) =>
                gestes.corrigerChiffreAffliction(a.index, position, valeur)}
              effacer={(position) => gestes.effacerChiffreAffliction(a.index, position)}
            />
          </li>
        {/each}
      </ul>
    {/if}

    <div class="offre">
      {#each vue.folies_offertes as f (f.id)}
        <button
          type="button"
          class="pilule"
          aria-pressed={folles.has(f.nom)}
          title={f.effet}
          onclick={() => gestes.folie(f.id)}
        >
          {f.nom}
        </button>
      {/each}
    </div>

    <div class="maison">
      <input
        class="saisie"
        type="text"
        bind:value={inventee}
        placeholder="Phobie — les profondeurs"
        aria-label="Affliction inventée à la table"
        onkeydown={(e) => e.key === 'Enter' && ajouterInventee()}
      />
      <button type="button" class="pilule" onclick={ajouterInventee}>Ajouter</button>
    </div>
    <p class="pied">
      Le livre invite le meneur à inventer ses propres Afflictions : celles-là
      n'ont pas de fiche, seulement le nom qu'on leur donne.
    </p>
  </div>
</div>

{#if c.alertes.length > 0}
  <ul class="alertes">
    {#each c.alertes as a, i (a + i)}
      <li>{a}</li>
    {/each}
  </ul>
{/if}

<style>
  /* Le libellé d'une Affliction de la table, éditable sans cérémonie. */
  .saisie.libelle {
    font: inherit;
    width: min(100%, 36ch);
  }
  .compteurs {
    display: grid;
    grid-template-columns: 1fr;
    gap: calc(var(--pas) * 2);
  }
  @media (min-width: 44rem) {
    .compteurs {
      grid-template-columns: repeat(6, 1fr);
    }
    .vie {
      grid-column: span 4;
    }
    .fortune {
      grid-column: span 2;
    }
    .mental {
      grid-column: span 6;
    }
  }
  .compteurs.mort {
    opacity: 0.7;
  }
  .compteurs .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
  .critique .vie,
  .mort .vie {
    border-color: var(--ko);
  }

  .compte {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 0.5);
    white-space: nowrap;
  }
  .gros {
    justify-content: center;
    margin: 0;
  }
  .seule {
    margin: 0;
  }
  .total,
  .max {
    font-size: 0.9rem;
    color: var(--ink-soft);
  }
  .jauge .compte {
    color: var(--ink);
  }
  .critique .vie :global(input),
  .mort .vie :global(input) {
    color: var(--ko);
  }

  .vitale :global(i) {
    background: var(--ko);
  }
  .barre :global(i) {
    transition: width 120ms ease-out;
  }
  .pied {
    margin: 0;
    font-size: 0.75rem;
    color: var(--ink-soft);
  }

  .statuts {
    margin-top: calc(var(--pas) * 2);
  }
  .vide {
    margin: 0;
    color: var(--ink-soft);
    font-size: 0.9rem;
  }
  .subis {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: calc(var(--pas) * 1.5);
  }
  .subis li {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px calc(var(--pas) * 2);
    padding-bottom: calc(var(--pas) * 1.5);
    border-bottom: 1px solid var(--line);
  }
  .subis li:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .nom {
    display: flex;
    align-items: center;
    gap: var(--pas);
    font-family: 'Cinzel', serif;
    font-size: 0.95rem;
  }
  .nom .badge {
    font-size: 0.55rem;
    padding: 1px 7px;
  }
  .resume {
    grid-column: 1 / -1;
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
  .lien {
    background: none;
    border: none;
    color: var(--ink-soft);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.8rem;
    padding: 0;
  }

  /* Les douze Statuts du livre, à portée de pouce : à table, on tombe à terre
     et on le note dans la seconde. */
  .offre {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 15rem), 1fr));
    gap: var(--pas);
    padding-top: calc(var(--pas) * 2);
    border-top: 1px solid var(--line);
  }


  /* Un Statut offert dit ce qu'il fait, sans qu'on ait à le survoler. */
  .offert {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    padding: calc(var(--pas) * 0.75) var(--pas);
    border: 1px solid var(--line);
    border-radius: var(--r);
    background: var(--surface-2);
    color: var(--ink);
    cursor: pointer;
  }
  .offert[aria-pressed='true'] {
    border-color: var(--accent);
    box-shadow: inset 3px 0 0 var(--accent);
  }
  .offert .titre {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: calc(var(--pas) * 0.5);
    font-weight: 600;
    font-size: 0.9rem;
  }
  .offert .effet {
    font-size: 0.78rem;
    line-height: 1.35;
    color: var(--ink-soft);
  }
  .maison {
    display: flex;
    gap: var(--pas);
    align-items: stretch;
  }
  .maison .saisie {
    flex: 1;
    min-width: 0;
  }

  .alertes {
    list-style: none;
    margin: calc(var(--pas) * 3) 0 0;
    padding: calc(var(--pas) * 2) calc(var(--pas) * 3);
    display: grid;
    gap: var(--pas);
    border-left: 3px solid var(--ko);
    background: var(--surface);
    border-radius: var(--r);
    font-size: 0.85rem;
    color: var(--ink-soft);
  }
</style>
